/**
 * Centralized Cache Management Utility
 * 
 * This file provides a single source of truth for all application cache keys
 * and a unified function to clear all user-related caches on sign-out.
 * 
 * Cache TTL Standards:
 * - Static data (equipment, rental houses): 10 minutes
 * - User data (projects, gear, tickets): 30-60 minutes  
 * - Settings/limits: 1-24 hours
 * - Real-time data: 0 stale time (always fresh)
 */

// All cache keys used in the application (localStorage keys)
export const CACHE_KEYS = {
  // User data caches
  PROJECTS: 'projects_cache',
  MY_GEAR: 'my_gear_cache_v1',
  SUPPORT_TICKETS: 'support_tickets_cache',
  TEAM_DATA: 'team_data_cache',
  SHARED_PROJECTS: 'team_shared_projects_cache',
  
  // Plan/subscription caches
  PLAN_LIMITS: 'plan_limits_cache',
  SUBSCRIPTION: 'subscription_details_cache',
  
  // Equipment/rental caches (static data)
  UNIFIED_EQUIPMENT: 'unified_equipment_cache_v1',
  PUBLIC_RENTAL_HOUSES: 'public_rental_houses_cache_v1',
  LINKED_RENTAL_PRICES: 'linked_rental_prices_cache_v1',
  
  // Settings caches
  SITE_SETTINGS: 'site_settings_cache',
  GALLERY: 'gallery_posts_cache',
  GUIDED_BUILDER_CONFIG: 'guided_builder_config_cache',
  
  // Guest usage caches (not cleared on sign-out)
  GUEST_PROJECTS: 'rigbuilder_projects',
  GUEST_AI_MESSAGES: 'guest_ai_messages',
  
  // Persistent user preferences (not cleared on sign-out)
  DISMISSED_ANNOUNCEMENTS: 'dismissed_announcements',
  PDF_EXPORT_SETTINGS: 'pdf_export_settings',
  ADMIN_WORKSPACE: 'admin_active_workspace',
  RIG_ASSISTANT_HISTORY: 'rig_assistant_chat_history',
} as const;

// Cache TTL standards (in milliseconds)
export const CACHE_TTL = {
  STATIC_DATA: 10 * 60 * 1000,      // 10 minutes - equipment, rental houses
  USER_DATA: 60 * 60 * 1000,        // 1 hour - my gear, projects, tickets
  SETTINGS: 24 * 60 * 60 * 1000,    // 24 hours - plan limits, site settings
  SUBSCRIPTION: 5 * 60 * 1000,      // 5 minutes - subscription details
} as const;

/**
 * Cache keys that should NOT be cleared on sign-out (guest data)
 */
const GUEST_CACHE_KEYS: ReadonlySet<string> = new Set([
  CACHE_KEYS.GUEST_PROJECTS,
  CACHE_KEYS.GUEST_AI_MESSAGES,
]);

/**
 * Clears all user-related caches from localStorage.
 * Should be called on sign-out to ensure clean state.
 * Preserves guest-specific caches (local projects, AI usage).
 */
export function clearAllUserCaches(): void {
  console.info('[Cache] Clearing all user caches...');
  
  Object.values(CACHE_KEYS).forEach(key => {
    // Skip guest-specific caches
    if (GUEST_CACHE_KEYS.has(key)) return;
    
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore storage errors
    }
  });
  
  console.info('[Cache] All user caches cleared');
}

/**
 * Clears a specific cache by key
 */
export function clearCache(key: keyof typeof CACHE_KEYS): void {
  try {
    localStorage.removeItem(CACHE_KEYS[key]);
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Helper to check if a cached item is still valid based on TTL
 */
export function isCacheValid(timestamp: number, ttlMs: number): boolean {
  return Date.now() - timestamp < ttlMs;
}
